import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, FormControl, Grid, IconButton, InputAdornment, MenuItem, TextField, Dialog, DialogActions, Tabs, Tab, AppBar } from '@material-ui/core';
import { CampaignRule, CampaignRuleSetup, EditCampaignRule } from '../../CashbackModel';
import { useStyles } from '../styles';
import { CheckIcon, CustomDialogTitle, FieldLabel, InfoIcon, InputControl, LightTooltip, MultiSelectControl } from '../../../../components';
import { useCommonStyles } from '../../../../styles';
import { a11yProps } from '../../../../utils';

interface Props {
  rule?: CampaignRule;
  campaignId: number;
  ruleSetup: CampaignRuleSetup;
  open: boolean;
  translation: any;
  handleCancel: () => void;
  onSave: (rule: EditCampaignRule, ruleId?: number) => void;
}

export const AddEditRuleDialog: FC<Props> = ({ rule, open, campaignId, translation, ruleSetup, handleCancel, onSave }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const [name, setName] = useState<string>();
  const [percentage, setPercentage] = useState<number>(0);
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>([translation.all_partnerships]);
  const [selectedPartnershipIds, setSelectedPartnershipIds] = useState<number[]>([]);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState<string[]>([translation.all_accounts]);
  const [selectedAccountTypeIds, setSelectedAccountTypeIds] = useState<number[]>([]);
  const [volumeType, setVolumeType] = useState<string>('USD');
  const [minVolume, setMinVolume] = useState<number>();
  const [maxVolume, setMaxVolume] = useState<number>();
  const [showError, setShowError] = useState<boolean>(false);
  const [showMinMaxError, setShowMinMaxError] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (window.innerWidth <= 600) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth <= 540) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    });
  }, []);

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setPercentage(rule.percentage);
      setMinVolume(rule.minVolume);
      setMaxVolume(rule.maxVolume);
      setVolumeType(rule.volumeType);
      if (rule.partnerships.length > 0 && rule.partnerships.length !== ruleSetup.partnerships.length) {
        const partnershipIds: number[] = [];
        const partnershipCodes: string[] = [];
        rule.partnerships.forEach((selected) => {
          const partnership = ruleSetup.partnerships.find((p) => p.partnershipId === selected.partnershipId);
          if (partnership !== undefined) {
            if (partnership.partnershipCode !== null) {
              partnershipCodes.push(partnership.partnershipCode);
            }
            if (partnership.partnershipId !== null) {
              partnershipIds.push(partnership.partnershipId);
            }
          }
        });
        setSelectedPartnershipIds(partnershipIds);
        setSelectedPartnerships(partnershipCodes);
      }

      if (rule.accountTypes.length > 0 && rule.accountTypes.length !== ruleSetup.accountTypes.length) {
        const typeIds: number[] = [];
        const accountTypes: string[] = [];
        rule.accountTypes.forEach((selected) => {
          const accountType = ruleSetup.accountTypes.find((p) => p.accountTypeId === selected.accountTypeId);
          if (accountType !== undefined) {
            if (accountType.accountTypeName != null) {
              accountTypes.push(accountType.accountTypeName);
            }

            if (accountType.accountTypeId != null) {
              typeIds.push(accountType.accountTypeId);
            }
          }
        });
        setSelectedAccountTypes(accountTypes);
        setSelectedAccountTypeIds(typeIds);
      }
    }
  }, [rule, ruleSetup]);

  const handlePartnershipsSelectChange = useCallback(
    async (event) => {
      const partnershipIds: number[] = [];
      let options = event.target.value as string[];
      if (options.length === 0) {
        options = [translation.all_partnerships];
      }

      if (options.length > 1) {
        if (options.includes(translation.all_partnerships)) {
          if (options[options.length - 1] === translation.all_partnerships) {
            options = options.filter((option) => option === translation.all_partnerships);
          } else {
            options = options.filter((option) => option !== translation.all_partnerships);
          }
        }
      }

      setSelectedPartnerships(options);
      options.forEach((option: any) => {
        if (option !== translation.all_partnerships) {
          const partnership = ruleSetup.partnerships.find((p) => p.partnershipCode === option);
          if (partnership !== undefined && partnership.partnershipId != null) {
            partnershipIds.push(partnership.partnershipId);
          }
        }
      });
      setSelectedPartnershipIds(partnershipIds);
    },
    [ruleSetup.partnerships, translation],
  );

  const handleTradingAccountSelectChange = useCallback(
    async (event) => {
      const typeIds: number[] = [];
      let options = event.target.value as string[];
      if (options.length === 0) {
        options = [translation.all_accounts];
      }

      if (options.length > 1) {
        if (options.includes(translation.all_accounts)) {
          if (options[options.length - 1] === translation.all_accounts) {
            options = options.filter((option) => option === translation.all_accounts);
          } else {
            options = options.filter((option) => option !== translation.all_accounts);
          }
        }
      }

      setSelectedAccountTypes(options);
      options.forEach((option: any) => {
        if (option !== translation.all_accounts) {
          const accountType = ruleSetup.accountTypes.find((a) => a.accountTypeName === option);
          if (accountType !== undefined && accountType.accountTypeId != null) {
            typeIds.push(accountType.accountTypeId);
          }
        }
      });
      setSelectedAccountTypeIds(typeIds);
    },
    [ruleSetup.accountTypes, translation],
  );

  const handleSave = (): void => {
    if (name === undefined || name === '') {
      setShowError(true);
    } else {
      const newRule = {
        cashbackCampaignId: campaignId,
        name,
        percentage: Number(percentage),
        partnerships: selectedPartnershipIds,
        accountTypes: selectedAccountTypeIds,
        minVolume: minVolume || 0,
        maxVolume: maxVolume === undefined || maxVolume === 0 || maxVolume.toString() === '' ? null : maxVolume,
        volumeType,
      };
      onSave(newRule, rule?.id);
    }
  };

  const validateNumber = (value: any) => {
    const regex = /([0-9]*[\\.|\\,]{0,1}[0-9]{0,2})/s;
    return value.match(regex)[0];
  };

  const handleTabChange = useCallback((event: any, newValue: number) => {
    setActiveTab(newValue);
    setVolumeType(newValue === 0 ? 'USD' : 'Lots');
  }, []);

  return (
    <Dialog onClose={handleCancel} open={open} classes={{ paper: classes.dialog }} disableEnforceFocus>
      <CustomDialogTitle title={rule ? translation.edit_rule : translation.new_rule} onClose={handleCancel} />
      <Grid container direction="column" alignItems="flex-start" className={commonClasses.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={isMobile ? 12 : 6}>
            <FieldLabel label={translation.campaign_rules} isRequired />
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              className={classes.inputField}
              type="text"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setShowError(false);
              }}
              placeholder={translation.specify}
              error={showError}
              helperText={showError ? translation.text_189 : ''}
            />
          </Grid>
          <Grid item xs={isMobile ? 12 : 6}>
            <FieldLabel label={translation.cashback_percentage}>
              <LightTooltip title={translation.cashback_percentage_tooltip} arrow placement="right-start">
                <IconButton size="small" style={{ marginTop: '-10px' }}>
                  <InfoIcon className={classes.infoIcon} />
                </IconButton>
              </LightTooltip>
            </FieldLabel>
            <FormControl variant="outlined" className={classes.formControlInput} margin="dense">
              <InputControl value={percentage} onChange={(event) => setPercentage(validateNumber(event.target.value))} type="number" inputProps={{ min: '0', max: '100', step: '0.01' }} />
            </FormControl>
          </Grid>
          {ruleSetup.partnerships && ruleSetup.partnerships.length !== 0 && (
            <Grid item xs={isMobile ? 12 : 6}>
              <FieldLabel label={translation.campaign_partnerships} isRequired />
              <FormControl variant="outlined" className={classes.formControl}>
                <MultiSelectControl value={selectedPartnerships} onChange={(event: any) => handlePartnershipsSelectChange(event)}>
                  <MenuItem className={selectedPartnerships.includes(translation.all_partnerships) ? classes.selectedOption : classes.option} value={translation.all_partnerships}>
                    <CheckIcon />
                    <span>{translation.all_partnerships}</span>
                  </MenuItem>
                  {ruleSetup.partnerships.map((partnership) => (
                    <MenuItem className={selectedPartnerships.includes(partnership.partnershipCode) ? classes.selectedOption : classes.option} key={partnership.partnershipId} value={partnership.partnershipCode ? partnership.partnershipCode : ''}>
                      <CheckIcon />
                      <span>{partnership.partnershipCode}</span>
                    </MenuItem>
                  ))}
                </MultiSelectControl>
              </FormControl>
            </Grid>
          )}
          {ruleSetup.accountTypes && ruleSetup.accountTypes.length !== 0 && (
            <Grid item xs={isMobile ? 12 : 6}>
              <FieldLabel label={translation.text_6399} isRequired />
              <FormControl variant="outlined" className={classes.formControl}>
                <MultiSelectControl value={selectedAccountTypes} onChange={(event: any) => handleTradingAccountSelectChange(event)}>
                  <MenuItem className={selectedAccountTypes.includes(translation.all_accounts) ? classes.selectedOption : classes.option} value={translation.all_accounts}>
                    <CheckIcon />
                    <span>{translation.all_accounts}</span>
                  </MenuItem>
                  {ruleSetup.accountTypes.map((accountType) => (
                    <MenuItem className={selectedAccountTypes.includes(accountType.accountTypeName) ? classes.selectedOption : classes.option} key={accountType.accountTypeId} value={accountType.accountTypeName ? accountType.accountTypeName : ''}>
                      <CheckIcon />
                      <span>{accountType.accountTypeName}</span>
                    </MenuItem>
                  ))}
                </MultiSelectControl>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <FieldLabel label={translation.client_trading_volume} isRequired>
              <LightTooltip title={translation.client_trading_volume_tooltip} arrow placement="right-start">
                <IconButton size="small" style={{ marginTop: '-10px' }}>
                  <InfoIcon className={classes.infoIcon} />
                </IconButton>
              </LightTooltip>
            </FieldLabel>
          </Grid>
          <AppBar position="static" className={commonClasses.tabs}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
              <Tab label="USD" value={0} {...a11yProps(0)} />
              <Tab label={translation.text_696} value={1} {...a11yProps(1)} />
            </Tabs>
          </AppBar>
          <Grid item xs={isMobile ? 12 : 6}>
            <FieldLabel label={translation.minimum_trading_volume} />
            <FormControl variant="outlined" className={classes.formControlInput} margin="dense">
              <InputControl
                value={minVolume}
                onChange={(event) => {
                  const newValue = event.target.value;
                  if (maxVolume && Number(maxVolume) <= Number(newValue)) {
                    setShowMinMaxError(true);
                  } else {
                    setShowMinMaxError(false);
                  }
                  setMinVolume(newValue);
                }}
                type="number"
                inputProps={{ min: 0 }}
                startAdornment={
                  <InputAdornment className={classes.inputAdornment} position="start">
                    {volumeType === 'USD' ? '$' : translation.text_696}
                  </InputAdornment>
                }
                onKeyPress={(event) => {
                  if (event?.key === '-' || event?.key === '+') {
                    event.preventDefault();
                  }
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={isMobile ? 12 : 6}>
            <FieldLabel label={translation.maximum_trading_volume} />
            <FormControl variant="outlined" className={classes.formControlInput} margin="dense">
              <InputControl
                value={maxVolume}
                onChange={(event) => {
                  const newValue = event.target.value;
                  if (newValue !== '' && minVolume && Number(newValue) <= Number(minVolume)) {
                    setShowMinMaxError(true);
                  } else {
                    setShowMinMaxError(false);
                  }
                  setMaxVolume(newValue);
                }}
                type="number"
                inputProps={{ min: 0 }}
                startAdornment={
                  <InputAdornment className={classes.inputAdornment} position="start">
                    {volumeType === 'USD' ? '$' : translation.text_696}
                  </InputAdornment>
                }
                onKeyPress={(event) => {
                  if (event?.key === '-' || event?.key === '+') {
                    event.preventDefault();
                  }
                }}
              />
            </FormControl>
          </Grid>
          {showMinMaxError && (
            <Grid item className={classes.errorMessage}>
              {translation.max_volume_should_not_be_less_min_volume}
            </Grid>
          )}
        </Grid>
      </Grid>
      <DialogActions className={classes.dialogAction}>
        <Button onClick={handleSave} startIcon={rule ? <CheckIcon /> : ''} className={`${commonClasses.activeDialogBtn} ${classes.btn}`}>
          {rule ? translation.text_10 : translation.add_rule}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
